<script lang="ts">
    import { createEventDispatcher, onMount } from 'svelte'
    import Fa from 'svelte-fa'
    import { faPaperPlane, faCopy, faRefresh, faSave } from '@fortawesome/free-solid-svg-icons'
    import { Appointment } from '../../modules/schedulerApi/appointments/appointment'
    import Zframe from '../_common/z/zframe.svelte'
    import dayjs from 'dayjs'
    import Signature from '../_common/Signature.svelte'
    import Zflow from '../_common/z/zflow.svelte'
    import { SendMessageTemplate } from '../_common/send-message.template'
    import type { GridButton } from '../_common/z/zgrid'
    import { encodeUuidForLink } from '../public/uuid-link-code'

    const dispatch = createEventDispatcher()

    export let appointmentId: string = null

    let loaded = false
    let entity: Appointment = new Appointment()
    let items = ''

    onMount(async () => {
        await loadEntity(appointmentId)
        await loadLink()
        loadActs()
        loaded = true
    })

    let acts: GridButton[] = []
    function loadActs() {
        if (!entity?.signed) {
            acts.push({
                callback: () => loadEntity(entity.appointmentId),
                label: 'Atualizar',
                icon: faRefresh,
            })

            acts.push({
                callback: sendMessage,
                label: 'Enviar',
                icon: faPaperPlane,
            })
            acts.push({
                callback: saveEntity,
                label: 'Salvar',
                icon: faSave,
            })
        }
        acts = acts
    }

    async function loadEntity(id: string) {
        try {
            items = ''
            entity = new Appointment()
            entity = await window.api.schedulers.appointments.getOne(id)
            entity.items = await window.api.utils.utils.assignUsersFromObject(entity.items)
            entity.items = await window.api.utils.utils.assignServicesFromObject(entity.items)
            entity.person = await window.api.events.persons.getOne(entity.personId)
            for (const item of entity.items) {
                if (items) items += ', '
                items += item.service.name
            }
            console.log('appointment', entity)
        } catch (error) {
            window.error(error)
        } finally {
        }
    }

    async function saveEntity() {
        if (entity.signed) {
            window.error('Este agendamento já foi assinado.')
            return
        }
        try {
            const signed = await window.api.schedulers.appointments.sign(entity.appointmentId, signBase64)
            console.log('signed', signed)
            dispatch('close')
        } catch (error) {
            window.error(error)
        }
    }

    let signBase64 = ''

    function signChange() {}

    let linkField
    let linkUrl: string = ''
    let linkPage: string = ''

    async function getPersonToken() {
        const response = await window.api.events.persons.LoginByLink(entity.personId)
        console.log('resp', response)
        if (!response) return null
        return response.linkToken
    }

    async function loadLink() {
        const token = await getPersonToken()
        if (!token) {
            window.error('Link indisponível')
            return
        }
        // Três uuid neste link — personId, linkToken e appointmentId — e ele é enviado por WhatsApp,
        // que passou a tratar uuid como chave pix aleatória e renderizar só o uuid no lugar do link.
        // base64url aqui, e não base58 como no checkout/assinatura de documento: o destino é o /login
        // do app do cliente, que já decodifica base64url por causa dos links do automation-api.
        // O appointmentId viaja DENTRO do "d" (destino), então quem o desfaz é a própria página de
        // destino no app do cliente, não o /login.
        linkPage =
            'login?p=' +
            encodeUuidForLink(entity.personId) +
            '&t=' +
            encodeUuidForLink(token) +
            '&d=appointments/sign?a=' +
            encodeUuidForLink(appointmentId)
        if (location.href.includes('127.0.0') || location.href.includes('localhost')) {
            linkUrl = 'http://localhost:5173/' + linkPage
        } else {
            linkUrl = 'https://agenda.zandu.com.br/' + linkPage
        }
    }

    function copyLink() {
        window.api.utils.utils.copyToClipboard(linkUrl)
        //navigator.clipboard.writeText(linkUrl)
        window.alert('Link copiado')
    }

    function sendMessage() {
        window.message(SendMessageTemplate.share_sign_02(linkPage, entity.personId))
    }
</script>

<div class="root">
    <Zflow class="m-3">
        <Zframe title="Assinatura" headline="Ao assinar você confirma o comparecimento e realização do(s) serviço(s) abaixo relacionados." {acts}>
            {#if entity?.signed}
                <div class="signed_img">
                    <img src={entity.sign?.sign} />
                </div>
            {:else}
                <Signature bind:image={signBase64} on:change={signChange} label="Assine com o dedo/mouse" />
            {/if}
        </Zframe>
        {#if entity?.signed}
            <Zframe title="Dados de validação">
                <div class="sign-line">
                    <div class="row">
                        <div class="col">
                            <span class="title-badge">IP do assinante</span>
                            <span>{entity.sign.fingerprint.requestIP || ''} / {entity.sign.fingerprint.forwardedIP || ''}</span>
                        </div>
                    </div>
                </div>
                <div class="sign-line">
                    <div class="row">
                        <div class="col">
                            <span class="title-badge">Data da assinatura</span>
                            <span>{dayjs(entity.sign.fingerprint.time).format('DD/MM/YYYY HH:mm:ss')}</span>
                        </div>
                    </div>
                </div>
                <div class="sign-line">
                    <div class="row">
                        <div class="col">
                            <span class="title-badge">Timestamp da assinatura</span>
                            <span>{entity.sign.fingerprint.time}</span>
                        </div>
                    </div>
                </div>
                <div class="sign-line">
                    <div class="row">
                        <div class="col">
                            <span class="title-badge">Metadados</span>
                            <span>{entity.sign.fingerprint.userAgent}</span>
                        </div>
                    </div>
                </div>
            </Zframe>
        {/if}

        <Zframe title="Dados do agendamento">
            <div class="sign-line">
                <div class="row">
                    <div class="col-12">
                        <span class="title-badge">Cliente</span>
                        <span>
                            {entity?.person?.name}
                        </span>
                    </div>
                </div>
            </div>
            <div class="sign-line">
                <div class="row">
                    <div class="col-12">
                        <span class="title-badge">Telefone</span><span>{entity?.person?.phone}</span>
                    </div>
                </div>
            </div>
            <div class="sign-line">
                <div class="row">
                    <div class="col-12">
                        <span class="title-badge">Documento</span><span>{entity?.person?.document || ''}</span>
                    </div>
                </div>
            </div>

            <div class="sign-line">
                <div class="row">
                    <div class="col-12">
                        <span class="title-badge">Data do agendamento</span>
                        <span>
                            {dayjs(entity?.start).format('DD/MM/YYYY')}
                        </span>
                    </div>
                </div>
            </div>

            <div class="sign-line">
                <div class="row">
                    <div class="col">
                        <span class="title-badge">Hora</span><span>{dayjs(entity?.start).format('HH:mm')}</span>
                    </div>
                </div>
            </div>

            <div class="sign-line">
                <div class="row">
                    <div class="col">
                        <span class="title-badge">Duração</span><span>{dayjs(entity?.end).diff(entity?.start, 'minute')} minutos</span>
                    </div>
                </div>
            </div>

            <div class="sign-line">
                <div class="row">
                    <div class="col">
                        <span class="title-badge">Serviços</span>
                        <span>{items}</span>
                    </div>
                </div>
            </div>
        </Zframe>
    </Zflow>
</div>

<style>
    .sign-line {
        font-size: 14px;
        padding: 8px;
        border-bottom: 1px solid #99999918;
    }
    .sign-line:last-child {
        border-bottom: 0px solid #99999918;
    }

    .signed_img {
        background-color: white;
        overflow: hidden;
        border-radius: 4px;
    }
</style>
